import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { io } from "socket.io-client";
import { FaClock, FaUsers, FaCheck, FaHandPaper, FaVideo, FaHandPointUp } from "react-icons/fa";
import { SkeletonLobby } from "../../Skeleton";
import API_URL from '../../../utils/api';
import "./Lobby.css";
export default function Lobby() {
  const { sessionId } = useParams(); // Changed from pairId to sessionId
  const [session, setSession] = useState(null);
  const [readyUsers, setReadyUsers] = useState([]);
  const [isReady, setIsReady] = useState(false);
  const [hasClickedMeetLink, setHasClickedMeetLink] = useState(false);
  const [userId] = useState(() => sessionStorage.getItem("userId") || localStorage.getItem("userId"));
  const [notification, setNotification] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const socketRef = useRef(null);
  const sessionRef = useRef(null);
  // Fetch session data
  useEffect(() => {
    const fetchSession = async () => {
      try {
        const response = await fetch(`${API_URL}/api/sessions/${sessionId}`);
        const data = await response.json();
        // Parse members if it's a string
        if (data.members && typeof data.members === 'string') {
          data.members = JSON.parse(data.members);
        }
        // Parse ready_users if it's a string
        if (data.ready_users && typeof data.ready_users === 'string') {
          data.ready_users = JSON.parse(data.ready_users);
        }
        setSession(data);
        sessionRef.current = data; // Store in ref for socket callback
        setReadyUsers(data.ready_users || []);
        setIsReady((data.ready_users || []).includes(String(userId)));
        setIsLoading(false);
        // Track user in session if socket is already connected
        if (socketRef.current && socketRef.current.connected) {
          const userName = data.members?.find(m => m.user_id === +userId)?.name;
          if (userName) {
            socketRef.current.emit('track-session', {
              sessionId,
              userId,
              userName
            });
          }
        }
      } catch (error) {
        }
    };
    fetchSession();
    const interval = setInterval(fetchSession, 3000); // Poll every 3 seconds as backup
    return () => clearInterval(interval);
  }, [sessionId, userId]);
  // Socket.io for real-time updates
  useEffect(() => {
    // Only create socket once - check if it already exists and is connected
    if (socketRef.current && socketRef.current.connected) {
      return;
    }
    if (!socketRef.current) {
      socketRef.current = io(API_URL, { 
        transports: ["websocket"],
        autoConnect: true,
        reconnection: true
      });
      socketRef.current.on("connect", () => {
        // Track user in session if we have session data
        const currentSession = sessionRef.current;
        if (currentSession) {
          const userName = currentSession.members?.find(m => m.user_id === +userId)?.name;
          if (userName) {
            socketRef.current.emit('track-session', {
              sessionId,
              userId,
              userName
            });
          }
        }
      });
      socketRef.current.on("disconnect", () => {
        });
      // Listen for new users joining
      socketRef.current.on(`session-${sessionId}-user-joined`, (data) => {
        setSession(prev => prev ? {
          ...prev,
          members: [...(prev.members || []), data.user]
        } : null);
      });
      // Listen for users leaving
      socketRef.current.on(`session-${sessionId}-user-left`, (data) => {
        // Show notification
        setNotification({
          type: 'leave',
          message: `${data.user.name} left the session`
        });
        setTimeout(() => setNotification(null), 4000);
        setSession(prev => prev ? {
          ...prev,
          members: (prev.members || []).filter(m => 
            m.user_id !== data.user.user_id && m.name !== data.user.name
          )
        } : null);
      });
      // Listen for session ended (no members left)
      socketRef.current.on(`session-${sessionId}-ended`, () => {
        alert('This session has ended because all participants left.');
        navigate('/dashboard');
      });
      // Listen for users marking ready
      socketRef.current.on(`session-${sessionId}-user-ready`, (data) => {
        setReadyUsers(prev => {
          if (!prev.includes(data.userId)) {
            return [...prev, data.userId];
          }
          return prev;
        });
      });
      // Listen for all users ready - redirect to virtual room
      socketRef.current.on(`session-${sessionId}-all-ready`, (data) => {
        // Untrack before navigating (this is intentional navigation, not leaving)
        if (socketRef.current) {
          socketRef.current.emit('untrack-session');
        }
        navigate(`/virtual-room/${sessionId}`);
      });
    }
    return () => {
      // Only disconnect on actual unmount, not on React strict mode or state changes
      // We'll handle disconnection in the beforeunload event instead
    };
  }, [sessionId, navigate, userId]);
  const handleMeetLinkClick = (e) => {
    e.preventDefault();
    setHasClickedMeetLink(true);
    if (session?.meet_link) {
      window.open(session.meet_link, '_blank', 'noopener,noreferrer');
    }
  };
  const handleMarkReady = async () => {
    // Mark user as ready
    if (isReady) return; // Already marked ready
    try {
      const response = await fetch(`${API_URL}/api/sessions/${sessionId}/ready`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId })
      });
      const data = await response.json();
      if (data.ok) {
        setIsReady(true);
        setReadyUsers(prev => {
          if (!prev.includes(String(userId))) {
            return [...prev, String(userId)];
          }
          return prev;
        });
        if (data.allReady) {
          }
      }
    } catch (error) {
      }
  };
  const handleLeaveSession = async () => {
    const confirmLeave = window.confirm('Are you sure you want to leave this session?');
    if (!confirmLeave) return;
    try {
      const userName = session.members?.find(m => m.user_id === +userId)?.name;
      const response = await fetch(`${API_URL}/api/sessions/${sessionId}/leave`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          userId,
          userName
        })
      });
      const data = await response.json();
      if (data.ok) {
        // Untrack before leaving
        if (socketRef.current) {
          socketRef.current.emit('untrack-session');
        }
        navigate('/dashboard');
      } else {
        alert(data.message || 'Failed to leave session');
      }
    } catch (error) {
      alert('Failed to leave session: ' + error.message);
    }
  };
  if (!session || isLoading) {
    return (
      <div className="lobby-root">
        <SkeletonLobby />
      </div>
    );
  }
  const members = session.members || [];
  const maxParticipants = session.max_participants || 6;
  const minParticipants = 2; // IntelliMe requires minimum 2 participants
  const participantCount = members.length;
  const readyCount = readyUsers.length;
  const hasMinParticipants = participantCount >= minParticipants;
  const hasEvenParticipants = participantCount % 2 === 0; // Must have even number for pairing
  const canStart = hasMinParticipants && hasEvenParticipants;
  const allReady = canStart && participantCount > 0 && readyCount === participantCount;
  return (
    <div className="lobby-root">
      {/* Notification Toast */}
      {notification && (
        <div className={`lobby-notification ${notification.type}`}>
          {notification.type === 'leave' && <FaHandPaper /> }
          {notification.message}
        </div>
      )}
      <div className="lobby-header">
        <h2>{session.name}</h2>
        <div className="lobby-count">
          <span className="count-badge">{participantCount}/{maxParticipants}</span>
          <span className="count-label">participants</span>
        </div>
      </div>
      <div className="lobby-content">
        {/* Left Panel */}
        <div className="lobby-left-panel">
          <div className="lobby-meet-section">
            <div className="lobby-instruction-message">
              <FaVideo /> Step 1: Click the link below to join the virtual call
            </div>
            <a 
              href={session.meet_link} 
              target="_blank" 
              rel="noopener noreferrer"
              className="lobby-meet-link"
              onClick={handleMeetLinkClick}
            >
              Join Video Call
            </a>
            {hasClickedMeetLink && (
              <div className="meet-link-confirmed"><FaCheck /> Meet link opened! Now click "I'm Ready" below.</div>
            )}
          </div>
          <div className="lobby-participants">
            <h3><FaUsers /> Participants</h3>
            <div className="participants-list">
              {members.map((member, index) => {
                const memberUserId = String(member.user_id || member.userId || '');
                const isUserReady = readyUsers.includes(memberUserId);
                return (
                  <div key={index} className="participant-item">
                    <div className="participant-avatar">
                      {member.name?.charAt(0).toUpperCase()}
                    </div>
                    <div className="participant-info">
                      <div className="participant-name">{member.name}</div>
                      {member.user_id === session.host_id && (
                        <span className="participant-host-badge">Host</span>
                      )}
                    </div>
                    <div className="participant-status">
                      {isUserReady ? (
                        <span className="status-ready"><FaCheck /> Ready</span>
                      ) : (
                        <span className="status-waiting">Waiting...</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        {/* Right Panel */}
        <div className="lobby-right-panel">
          <div className="lobby-ready-section">
            <div className="ready-count">
              <div className="ready-count-text">
                {readyCount} of {participantCount} ready
              </div>
            </div>
            {!hasClickedMeetLink && !isReady && (
              <div className="lobby-instruction">
                <FaHandPointUp /> Step 1: Click the video call link to join
              </div>
            )}
            {hasClickedMeetLink && !isReady && (
              <div className="lobby-instruction" style={{ background: '#e8f5e9', borderColor: '#27ae60', color: '#27ae60' }}>
                <FaCheck /> Step 2: Click "I'm Ready" to continue
              </div>
            )}
            <div className="lobby-actions">
              <button 
                className={`lobby-ready-btn ${isReady ? 'ready' : ''} ${!hasClickedMeetLink ? 'disabled' : ''}`}
                onClick={handleMarkReady}
                disabled={!hasClickedMeetLink || isReady}
              >
                {isReady ? <><FaCheck /> Ready</> : 'I\'m Ready'}
              </button>
              <button 
                className="lobby-leave-btn"
                onClick={handleLeaveSession}
              >
                Leave Session
              </button>
            </div>
            {!hasMinParticipants && (
              <div className="lobby-waiting-for-others" style={{ background: '#fff3e0', borderColor: '#ff9800' }}>
                <div className="waiting-icon" style={{ color: '#ff9800' }}><FaUsers /></div>
                <div className="waiting-text" style={{ color: '#e65100' }}>
                  Waiting for more participants... ({participantCount}/{minParticipants} minimum required)
                </div>
              </div>
            )}
            {hasMinParticipants && !hasEvenParticipants && (
              <div className="lobby-waiting-for-others" style={{ background: '#fff3e0', borderColor: '#ff9800' }}>
                <div className="waiting-icon" style={{ color: '#ff9800' }}><FaUsers /></div>
                <div className="waiting-text" style={{ color: '#e65100' }}>
                  Waiting for one more participant to join... (need even number for pairing: {participantCount} → {participantCount + 1})
                </div>
              </div>
            )}
            {isReady && canStart && !allReady && (
              <div className="lobby-waiting-for-others">
                <div className="waiting-icon"><FaClock /></div>
                <div className="waiting-text">
                  Waiting for{' '}
                  {members
                    .filter(m => {
                      const memberUserId = String(m.user_id || m.userId || '');
                      return !readyUsers.includes(memberUserId);
                    })
                    .map(m => m.name)
                    .join(', ')}{' '}
                  to get ready...
                </div>
              </div>
            )}
            {allReady && (
              <div className="all-ready-message">
                <FaCheck style={{ marginRight: '8px' }} /> Everyone is ready! Starting session...
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
